import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, BarChart3, Settings, FileText, Zap, Users, Building2, TrendingUp,
} from 'lucide-react';

/* ── Hero slides ─────────────────────────────────────────────────── */
const slides = [
  {
    tag: 'NEXT-GENERATION FINANCIAL INFRASTRUCTURE',
    lines: ['Precision Finance.', 'Intelligent Systems.', 'Scalable Control.'],
    accentIdx: 2,
    body: 'Boston Federal delivers disciplined financial infrastructure, intelligent automation, and full-stack visibility to help businesses operate with precision and scale with confidence.',
    cta1: { label: 'Explore Capabilities', to: '/capabilities' },
    cta2: { label: 'Schedule a Consultation', to: '/contact' },
    pills: ['Audit-Ready', 'Bankable', 'Fundable', 'Reportable'],
  },
  {
    tag: 'PAYROLL & WORKFORCE COMPLIANCE',
    lines: ['Complex Payroll.', 'Every Compliance.', 'Handled Right.'],
    accentIdx: 2,
    body: 'Specialized payroll management for union contractors, certified payroll, prevailing wage, and federal/state funded projects. We handle the complexity.',
    cta1: { label: 'View Payroll Services', to: '/payroll-compliance' },
    cta2: { label: 'Talk to a Specialist', to: '/contact' },
    pills: ['Union Payroll', 'Certified Payroll', 'Prevailing Wage'],
  },
  {
    tag: 'PORTFOLIO SUPPORT & CAPITAL PARTNERS',
    lines: ['From Operations', 'to Capital-Ready.', 'One Partnership.'],
    accentIdx: 1,
    body: 'Boston Federal helps portfolio companies become reportable, fundable, bankable, and audit-ready. We build the infrastructure capital providers require.',
    cta1: { label: 'View Capital Partners', to: '/capital-partners' },
    cta2: { label: 'Get in Touch', to: '/contact' },
    pills: ['Reportable', 'Fundable', 'Bankable', 'Audit-Ready'],
  },
];

/* ── Capabilities for tab section ────────────────────────────────── */
const capabilities = [
  {
    icon: Shield,
    label: 'Financial Control',
    subtitle: 'Operational Discipline',
    title: 'Precision Control at Every Layer',
    desc: 'Disciplined account structures, internal controls, and oversight systems that give you complete visibility into every dollar. Enterprise-grade financial control built for your business.',
    points: ['Account structure design', 'Internal controls', 'Monthly close management', 'Financial oversight frameworks'],
    link: '/capabilities',
  },
  {
    icon: TrendingUp,
    label: 'Strategic Planning',
    subtitle: 'Forward-Looking Vision',
    title: 'Financial Planning Built for Growth',
    desc: 'Forward-looking models, scenario planning, and KPI frameworks aligned to your business goals. We turn financial data into confident strategic decisions.',
    points: ['Budget development', 'Cash flow forecasting', 'Scenario modeling', 'KPI design & tracking'],
    link: '/capabilities',
  },
  {
    icon: Building2,
    label: 'Infrastructure',
    subtitle: 'Systems & Architecture',
    title: 'Financial Infrastructure That Scales',
    desc: 'Systems, processes, and organizational structures designed for scalability, compliance, and long-term operational health that can withstand growth and capital events.',
    points: ['System setup & optimization', 'Process documentation', 'Systems architecture', 'Scalable operational design'],
    link: '/capabilities',
  },
  {
    icon: Settings,
    label: 'Accounting Ops',
    subtitle: 'Daily Execution',
    title: 'Reliable Operations. Every Month.',
    desc: 'Month-end close, reconciliations, and real-time bookkeeping that keeps your financial engine running. Consistent, accurate, and dependable.',
    points: ['Month-end close process', 'Bank reconciliations', 'AP/AR management', 'General ledger maintenance'],
    link: '/capabilities',
  },
  {
    icon: BarChart3,
    label: 'Reporting',
    subtitle: 'Actionable Intelligence',
    title: 'Reporting That Drives Decisions',
    desc: 'Custom dashboards, management packages, and reporting frameworks that make your numbers actionable, auditable, and presentable to any stakeholder.',
    points: ['Custom financial reporting', 'Management packages', 'Investor/lender reports', 'Real-time dashboards'],
    link: '/capabilities',
  },
  {
    icon: Zap,
    label: 'Automation',
    subtitle: 'Future-Ready Systems',
    title: 'Intelligent Automation That Delivers',
    desc: 'Workflow automation, intelligent systems, and proprietary tools that reduce manual work and accelerate financial operations with precision.',
    points: ['Workflow automation', 'Proprietary tools', 'Process optimization', 'Technology integration'],
    link: '/capabilities',
  },
  {
    icon: Users,
    label: 'Payroll & Compliance',
    subtitle: 'Complex Compliance',
    title: 'Complex Payroll. Handled with Precision.',
    desc: 'Specialized payroll for union contractors, certified payroll, prevailing wage, and federal/state project requirements. We handle the complexity.',
    points: ['Union contractor payroll', 'Certified payroll reporting', 'Prevailing wage', 'Job-cost allocation'],
    link: '/payroll-compliance',
    linkLabel: 'View Payroll Services',
  },
  {
    icon: FileText,
    label: 'Bankability',
    subtitle: 'Capital Access',
    title: 'Ready for Capital. Ready to Grow.',
    desc: 'Preparing businesses to be fundable, bankable, audit-ready, and positioned to access commercial capital with the credibility lenders and investors require.',
    points: ['Lender package prep', 'Audit readiness', 'Financial credibility', 'Capital access strategy'],
    link: '/capital-partners',
    linkLabel: 'View Capital Partners',
  },
];

const trustedOrgs = ['NEXORA', 'ALTITUDE', 'VERIDIAN', 'SUMMIT RIDGE', 'LUMENIX', 'PRAETORIAN'];

/* ── Slide variants ──────────────────────────────────────────────── */
const slideVariants = {
  enter: dir => ({ x: dir > 0 ? '5%' : '-5%', opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  exit: dir => ({ x: dir > 0 ? '-3%' : '3%', opacity: 0, transition: { duration: 0.35 } }),
};

export default function Home() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setDirection(1);
      setCurrent(prev => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(t);
  }, []);

  const goTo = (idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
  };

  const slide = slides[current];
  const cap = capabilities[activeTab];
  const CapIcon = cap.icon;

  return (
    <div>
      {/* ── Sliding Hero ──────────────────────────────────────────── */}
      <section className="grid-bg min-h-[90vh] flex flex-col justify-center pt-16 overflow-hidden" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={current}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="max-w-4xl"
            >
              <div className="inline-flex items-center rounded-full bg-[#0ABFBC]/10 border border-[#0ABFBC]/20 text-[#09A09D] px-4 py-1.5 text-[10px] font-bold tracking-[0.18em] mb-8">
                {slide.tag}
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-[68px] font-bold text-[#1A2234] leading-[1.05] tracking-tight mb-6">
                {slide.lines.map((line, i) => (
                  <span key={i} className={i === slide.accentIdx ? 'text-[#0ABFBC] block' : 'block'}>
                    {line}
                  </span>
                ))}
              </h1>
              <p className="text-lg text-[#475569] max-w-xl leading-relaxed mb-8">
                {slide.body}
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to={slide.cta1.to}
                  data-testid="hero-primary-cta"
                  className="bg-[#0ABFBC] text-white px-7 py-3 rounded-md font-semibold hover:bg-[#09A09D] transition-colors flex items-center gap-2"
                >
                  {slide.cta1.label} <ArrowRight size={17} />
                </Link>
                <Link
                  to={slide.cta2.to}
                  data-testid="hero-secondary-cta"
                  className="border-2 border-[#1A2234] text-[#1A2234] px-7 py-3 rounded-md font-semibold hover:bg-[#1A2234] hover:text-white transition-all flex items-center gap-2"
                >
                  {slide.cta2.label} <ArrowRight size={17} />
                </Link>
              </div>
              <div className="flex flex-wrap gap-2.5" data-testid="hero-feature-pills">
                {slide.pills.map(p => (
                  <span key={p} className="rounded-full bg-[#0ABFBC]/10 text-[#09A09D] px-4 py-1.5 text-xs font-bold tracking-wide border border-[#0ABFBC]/20">
                    {p}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Slide indicators + progress */}
          <div className="mt-12 flex items-center gap-4">
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  data-testid={`slide-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${i === current ? 'w-8 bg-[#0ABFBC]' : 'w-2 bg-slate-200 hover:bg-slate-300'}`}
                />
              ))}
            </div>
            <div className="flex-1 max-w-48 h-px bg-slate-200 relative overflow-hidden">
              <AnimatePresence>
                <motion.div
                  key={current}
                  className="absolute top-0 left-0 h-full bg-[#0ABFBC]"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 5.5, ease: 'linear' }}
                />
              </AnimatePresence>
            </div>
            <span className="text-xs text-slate-400 font-medium tabular-nums">
              {current + 1} / {slides.length}
            </span>
          </div>
        </div>
      </section>

      {/* ── Trusted Logos ─────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100 py-10" data-testid="trusted-logos">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-7">
            Trusted by Innovative Organizations
          </p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4">
            {trustedOrgs.map(org => (
              <span key={org} className="text-[#1A2234] font-bold text-xs tracking-[0.15em] opacity-25 hover:opacity-50 transition-opacity">
                {org}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Capabilities Tab Section ──────────────────────────────── */}
      <section className="bg-white py-20" data-testid="capabilities-tab-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="mb-10">
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">
              Core Capabilities
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold text-[#1A2234] leading-tight tracking-tight">
              Built for Financial Control<br />at Every Layer
            </h2>
          </div>

          {/* Tab Bar */}
          <div className="border-b border-slate-100 mb-0">
            <div className="flex overflow-x-auto no-scrollbar">
              {capabilities.map((c, i) => (
                <button
                  key={c.label}
                  onClick={() => setActiveTab(i)}
                  data-testid={`cap-tab-${i}`}
                  className={`px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors duration-200 ${
                    i === activeTab
                      ? 'text-[#0ABFBC] border-[#0ABFBC]'
                      : 'text-[#475569] border-transparent hover:text-[#1A2234]'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content Panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="py-14 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center"
            >
              {/* Left */}
              <div className="lg:col-span-3">
                <div className="w-11 h-11 rounded-xl bg-[#0ABFBC]/10 flex items-center justify-center mb-5">
                  <CapIcon size={22} strokeWidth={1.5} className="text-[#0ABFBC]" />
                </div>
                <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">{cap.subtitle}</p>
                <h3 className="text-3xl font-bold text-[#1A2234] mb-4 leading-tight">{cap.title}</h3>
                <p className="text-[#475569] leading-relaxed mb-7">{cap.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
                  {cap.points.map(p => (
                    <div key={p} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0ABFBC] flex-shrink-0 mt-[7px]" />
                      <span className="text-[#475569] text-sm">{p}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={cap.link}
                  data-testid={`cap-tab-cta-${activeTab}`}
                  className="inline-flex items-center gap-1.5 text-[#0ABFBC] font-bold text-xs uppercase tracking-wider hover:gap-3 transition-all"
                >
                  {cap.linkLabel || 'Schedule a Consultation'} <ArrowRight size={13} />
                </Link>
              </div>

              {/* Right – visual */}
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-52 h-52 rounded-full bg-[#0ABFBC]/5 flex items-center justify-center">
                  <div className="w-36 h-36 rounded-full bg-[#0ABFBC]/8 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[#0ABFBC]/12 flex items-center justify-center">
                      <CapIcon size={40} strokeWidth={0.9} className="text-[#0ABFBC]/60" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Dark CTA ──────────────────────────────────────────────── */}
      <section className="dark-grid-bg py-24" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Get Started</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-5">
              Ready to Build Your<br />Financial Infrastructure?
            </h2>
            <p className="text-slate-400 text-lg max-w-lg mx-auto mb-10">
              Boston Federal partners with businesses ready to operate with discipline, visibility, and control.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="cta-get-in-touch" className="bg-[#0ABFBC] text-white px-8 py-3.5 rounded-md font-semibold hover:bg-[#09A09D] transition-colors flex items-center gap-2">
                Get in Touch <ArrowRight size={17} />
              </Link>
              <Link to="/capabilities" data-testid="cta-view-capabilities" className="border-2 border-white/40 text-white px-8 py-3.5 rounded-md font-semibold hover:bg-white hover:text-[#0B1120] transition-all flex items-center gap-2">
                View Capabilities <ArrowRight size={17} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
